import type { ReactNode } from 'react'
import React, { useEffect } from 'react'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import Head from 'next/head'
import { twJoin } from 'tailwind-merge'
import { FAVICON_LINK_KEYS } from 'shared/constants'
import { AccessDenied } from 'shared/errors/access-denied'
import { featureFlagKeys } from 'shared/feature-flags/feature-keys'
import { useRestrictedAccessNotification } from 'shared/hooks/use-restricted-access-notification'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { CommunityOwnerBlockedModal } from 'modules/community/components/community-owner-blocked-modal'
import Sidebar from 'modules/community/components/sidebar'
import JoinCommunityProvider from 'modules/community/context/join-community-provider'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import Header from 'modules/header'
import MobileNav from 'modules/header/components/mobile-nav'
import { useBrand } from 'modules/white-label/contexts/brand-context'

interface Props extends React.PropsWithChildren {
  domainAssets?: DomainAssetsInterface
}

const CommunityLayout = ({ children, domainAssets }: Props): ReactNode => {
  const { data, error } = useCommunityWithTopics()
  useRestrictedAccessNotification()

  const { brand } = useBrand()
  const isMaintenanceBannerEnabled = useFeatureIsOn(featureFlagKeys.enableMaintenanceBanner)

  useEffect(() => {
    if (error instanceof AccessDenied) {
      globalThis.location.replace('/community/403')
    }
  }, [error])

  return (
    <>
      <Head>
        <title>{data?.name}</title>
        {domainAssets?.faviconUrl && (
          <link key={FAVICON_LINK_KEYS.icon} rel="icon" href={domainAssets.faviconUrl} />
        )}
      </Head>
      <div className="min-h-screen bg-bg-page">
        <Header
          logoSrc={domainAssets?.logoUrl || brand.logo?.path}
          className="w-full"
          headerContainerClassName="mx-auto max-w-360 px-4 sm:px-6 md:px-14 lg:px-26"
        />
        <MobileNav />
        <JoinCommunityProvider>
          <div
            className={twJoin(
              'mx-auto flex max-w-360 gap-9 px-0 sm:px-6 md:px-14 lg:px-26 lg:py-9',
              isMaintenanceBannerEnabled && 'lg:pt-16',
            )}
          >
            <Sidebar />
            <main className="w-full min-w-0">{children}</main>
          </div>
        </JoinCommunityProvider>
        <CommunityOwnerBlockedModal />
      </div>
    </>
  )
}

export const getCommunityLayout = (
  page: ReactNode,
  props: { domainAssets?: DomainAssetsInterface },
): ReactNode => <CommunityLayout domainAssets={props.domainAssets}>{page}</CommunityLayout>

export default CommunityLayout
